/**
 * Parses and normalizes phone numbers to a standard format
 * Removes all non-digit characters and validates the format
 * 
 * @param {string} phoneNumber - The raw phone number input
 * @returns {string} - Normalized phone number (10 digits for US numbers)
 * @throws {Error} - If phone number is invalid
 */
export function parsePhoneNumber(phoneNumber) {
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Handle US phone numbers (10 digits after removing country code if present)
  // If it starts with 1 and has 11 digits, remove the leading 1
  let normalizedDigits = digitsOnly;
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    normalizedDigits = digitsOnly.slice(1);
  }
  
  // Validate length (should be 10 digits for US)
  if (normalizedDigits.length !== 10) {
    throw new Error(`Invalid phone number length. Expected 10 digits, got ${normalizedDigits.length}`);
  }
  
  // Validate all characters are digits
  if (!/^\d{10}$/.test(normalizedDigits)) {
    throw new Error('Phone number must contain only digits and formatting characters');
  }
  
  return normalizedDigits;
}

/**
 * Formats a normalized phone number for display
 * Converts 10-digit number to (XXX) XXX-XXXX format
 * 
 * @param {string} phoneNumber - Normalized phone number (10 digits)
 * @returns {string} - Formatted phone number
 */
export function formatPhoneNumberForDisplay(phoneNumber) {
  const digits = phoneNumber.replace(/\D/g, '');
  
  if (digits.length !== 10) {
    return phoneNumber; // Return as-is if not exactly 10 digits
  }
  
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
